/*
 * Reporting-scope coverage — how "All Campaigns" says what it covers.
 *
 * A total labelled ALL CAMPAIGNS answers two questions a viewer cannot check
 * for themselves: how many campaigns are in it, and through which day. Both
 * used to be unstated, which is precisely why an advertiser total that stopped
 * four days early looked exactly like one that did not.
 *
 * Everything here is projected from `advertiser_reporting_scope_v1` — the ONE
 * governed scope contract the server resolves. Nothing is counted, inferred or
 * date-mathed locally: a second derivation is how two surfaces would state
 * different coverage for the same total.
 *
 * Client vocabulary only. No net, gross, fee, margin, markup, provider or
 * governance implementation language reaches these strings.
 *
 * Byte-identical across Cortex and Quantum: one implementation, or the two
 * portals start describing the same coverage differently.
 */

const MONTHS = Object.freeze([
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]);

/*
 * Date-only in, date-only out. Parsed by field rather than by `new Date()`,
 * which reads a bare YYYY-MM-DD as UTC midnight and can render the previous
 * day for a viewer west of it — the exact off-by-one this contract exists to
 * stop being possible.
 */
function formatDateOnly(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const month = MONTHS[Number(match[2]) - 1];
  if (!month) return "";
  return `${month} ${Number(match[3])}`;
}

function countIncludedCampaigns(scope) {
  if (Array.isArray(scope?.includedCampaignIds)) return scope.includedCampaignIds.length;
  if (Number.isFinite(Number(scope?.includedCampaignCount))) {
    return Number(scope.includedCampaignCount);
  }
  return 0;
}

/**
 * @param {object|null} scope `advertiser_reporting_scope_v1`
 * @returns {{
 *   campaignCount: number,
 *   campaignsLabel: string,
 *   throughLabel: string,
 *   exceptionLabel: string,
 *   text: string,
 * }|null} null when there is no governed scope to describe — the caller renders
 *   nothing rather than a coverage claim it cannot substantiate.
 */
export function describeReportingScopeCoverage(scope) {
  const campaignCount = countIncludedCampaigns(scope);
  const through = formatDateOnly(scope?.reportingThrough || scope?.window?.end);
  if (!campaignCount && !through) return null;

  const campaignsLabel = campaignCount
    ? `${campaignCount} campaign${campaignCount === 1 ? "" : "s"} included`
    : "";
  const throughLabel = through ? `Reporting through ${through}` : "";
  /*
   * Delivery observed outside an approved flight is INCLUDED in the total and
   * named as the exception it is — never filtered out, and never left
   * unlabelled inside a number the viewer would otherwise read as in-flight.
   */
  const exceptionLabel = Array.isArray(scope?.outOfFlightDelivery?.campaignIds)
    && scope.outOfFlightDelivery.campaignIds.length
    ? "Includes delivery outside an approved flight"
    : "";

  return {
    campaignCount,
    campaignsLabel,
    throughLabel,
    exceptionLabel,
    text: [campaignsLabel, throughLabel, exceptionLabel].filter(Boolean).join(" · "),
  };
}

export default describeReportingScopeCoverage;
